import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog, type MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, from, type Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { NotificationService } from '../../modules/notifications/notification.service';
import { type User, type UserInfo } from '../../modules/user-management/users/user.model';
import { type ResponseMessage } from '../../shared/models/common.models';
import { SharedService } from '../../shared/services/shared.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private sharedService = inject(SharedService);
  private notificationService = inject(NotificationService);
  private dialog = inject(MatDialog);

  readonly tokenKey = 'token';
  private readonly refreshTokenKey = 'refresh_token';
  private readonly tokenExpirationKey = 'token_expiration';
  private readonly refreshTokenExpirationKey = 'refresh_token_expiration';
  private readonly refreshMutexName = 'auth-refresh-lock';
  private refreshInFlight$?: Observable<boolean>;
  private loggedInStatus = new BehaviorSubject<boolean>(this.isLoggedIn());
  readonly isLoggedIn$ = this.loggedInStatus.asObservable();
  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  readonly userInfo$ = this.userInfoSubject.asObservable();

  constructor() {
    this.syncLoginStateFromStorage();
  }

  isLoggedInSync(): boolean {
    return this.loggedInStatus.value;
  }

  isLoggedIn(): boolean {
    const token = localStorage.getItem(this.tokenKey);
    return !!token && !this.isTokenExpired();
  }

  private isTokenExpired(): boolean {
    const expiration = localStorage.getItem(this.tokenExpirationKey);
    return !expiration || new Date(parseInt(expiration, 10)) < new Date();
  }

  private canUseWebLocks(): boolean {
    return typeof navigator !== 'undefined' && 'locks' in navigator && !!navigator.locks;
  }

  private normalizeBearerToken(token: string | null): string {
    if (!token) {
      return '';
    }
    return token.startsWith('Bearer ') ? token.substring('Bearer '.length).trim() : token.trim();
  }

  private performRefreshRequest(refreshToken: string): Observable<boolean> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      RefreshToken: refreshToken
    });

    return this.http.post<any>(`${environment.apiBaseUrl}/REFRESHACCESSTOKEN`, {}, { headers, observe: 'response' }).pipe(
      tap(response => {
        const accessToken = response.headers.get('authorization');
        const expiration = response.headers.get('acesstokenexpirytime');

        if (!accessToken || !expiration) {
          console.error('refreshToken: Missing tokens or expiration in the response.');
          throw new Error('Missing tokens or expiration in the refresh response.');
        }

        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem(this.tokenExpirationKey, expiration.toString());
        this.loggedInStatus.next(true);
      }),
      map(() => true),
      catchError(err => {
        console.error('Token refresh failed:', err);
        return throwError(() => err);
      })
    );
  }

  private refreshTokenWithWebLock(refreshToken: string, forceRefresh: boolean, staleAccessToken?: string): Observable<boolean> {
    return from(
      navigator.locks.request(this.refreshMutexName, async () => {
        const currentToken = localStorage.getItem(this.tokenKey);
        const currentTokenNormalized = this.normalizeBearerToken(currentToken);
        const staleTokenNormalized = this.normalizeBearerToken(staleAccessToken ?? null);
        const tokenAlreadyRotated = !!staleTokenNormalized && !!currentTokenNormalized && currentTokenNormalized !== staleTokenNormalized;

        if (tokenAlreadyRotated && !this.isTokenExpired()) {
          return true;
        }

        if (!forceRefresh && !this.isTokenExpired()) {
          return true;
        }

        return firstValueFrom(this.performRefreshRequest(refreshToken));
      })
    ).pipe(switchMap(result => from(Promise.resolve(result))));
  }

  private syncLoginStateFromStorage(): void {
    this.loggedInStatus.next(this.isLoggedIn());
  }

  /**
   * @param username - email address or mobile number with country code prefix (e.g. "919178367890")
   * @param password - password or OTP value
   * @param usePassword - true for password auth, false for OTP auth (default: true)
   */
  login(username: string, password: string, usePassword = true): Observable<any> {
    return this.http.post<any>(`${environment.apiBaseUrl}/login`, { username, password, usePassword }, { observe: 'response' }).pipe(
      tap(response => {
        const accessToken = response.headers.get('authorization');
        const refreshToken = response.headers.get('refreshtoken');
        const expiration = response.headers.get('acesstokenexpirytime');
        const refreshExpiration = response.headers.get('refreshtokenexpirytime');

        if (accessToken && refreshToken && expiration && refreshExpiration) {
          this.setSession({ accessToken, refreshToken, expiration, refreshExpiration });
          this.loggedInStatus.next(true);
        } else {
          throw new Error('Invalid login response: Missing tokens or expiration.');
        }
      }),
      catchError(error => throwError(() => error))
    );
  }

  private setSession(authResult: any): void {
    const { accessToken, refreshToken, expiration, refreshExpiration } = authResult;

    if (!accessToken || !refreshToken || !expiration || !refreshExpiration) {
      throw new Error('Missing tokens or expiration in the authentication result.');
    }

    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
    localStorage.setItem(this.tokenExpirationKey, expiration.toString());
    localStorage.setItem(this.refreshTokenExpirationKey, refreshExpiration.toString());

    this.loggedInStatus.next(true);
    this.router.navigate(['/']);
  }

  refreshToken(options?: { forceRefresh?: boolean; staleAccessToken?: string }): Observable<boolean> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = localStorage.getItem(this.refreshTokenKey);
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available.'));
    }

    const forceRefresh = options?.forceRefresh === true;
    const staleAccessToken = options?.staleAccessToken;
    const refreshExecution$ = this.canUseWebLocks()
      ? this.refreshTokenWithWebLock(refreshToken, forceRefresh, staleAccessToken)
      : forceRefresh || this.isTokenExpired()
        ? this.performRefreshRequest(refreshToken)
        : from(Promise.resolve(true));

    this.refreshInFlight$ = refreshExecution$.pipe(
      catchError(err => {
        this.logout();
        return throwError(() => err);
      }),
      finalize(() => {
        this.refreshInFlight$ = undefined;
      }),
      shareReplay(Number('1'))
    );

    return this.refreshInFlight$;
  }

  logout(): void {
    const accessToken = localStorage.getItem(this.tokenKey);
    if (!accessToken) {
      this.performLocalLogout(true);
      return;
    }

    const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`);
    this.http.post(`${environment.apiBaseUrl}/logout`, {}, { headers }).subscribe({
      next: () => this.performLocalLogout(true),
      error: () => this.performLocalLogout(true)
    });
  }

  forgotPassword(username: string): Observable<ResponseMessage> {
    return this.http.get<ResponseMessage>(`${environment.apiBaseUrl}/forgotPassword`, {
      params: new HttpParams().set('username', username),
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  replaceForgottenPassword(newPassword: string, password: string, username: string): Observable<any> {
    return this.http.post(
      `${environment.apiBaseUrl}/replaceForgottenPassword`,
      { newPassword, password, username },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    );
  }

  performLocalLogout(reroute: boolean): void {
    // Close any open Material dialogs/modals. performLocalLogout is the single chokepoint for every
    // logout path (explicit logout, 401/session expiry, pre-reset cleanup), so doing it here covers
    // them all — no per-dialog wiring to forget. closeAll() is a no-op when nothing is open.
    this.dialog.closeAll();
    this.sharedService.updateClientTheme();
    this.notificationService.disconnect();
    this.userInfoSubject.next(null);
    localStorage.clear();
    // localStorage.clear() wipes the persisted theme key but leaves the .dark-mode class on <body>,
    // which would leave the (light-only) login screen rendering in dark mode. Reset to light so the
    // body class and the ThemeService signal stay in sync after logout.
    this.loggedInStatus.next(false);
    if (reroute) {
      this.router.navigate(['/login']);
    }
  }

  changePassword(username: string, password: string, newPassword: string): Observable<any> {
    const url = `${environment.apiBaseUrl}/changePassword`;
    const body = { username, password, newPassword };
    return this.http.post<any>(url, body);
  }

  requestOtp(username: string): Observable<ResponseMessage> {
    const params = new HttpParams().set('username', username);
    return this.http.get<ResponseMessage>(`${environment.apiBaseUrl}/requestOtp`, { params });
  }

  getUserRoles(): string[] {
    const stored = localStorage.getItem('userRoles');
    if (!stored) {
      return [];
    }
    try {
      const roles = JSON.parse(stored);
      if (Array.isArray(roles) && roles.every(role => typeof role === 'string')) {
        return roles;
      }
      return [];
    } catch (e) {
      console.error('Failed to parse user roles', e);
      return [];
    }
  }

  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.some(role => userRoles.includes(role.replace('ROLE_', '')));
  }

  hasAllRoles(roles: string[]): boolean {
    if (roles.length === 0) {
      return true;
    }
    const userRoles = this.getUserRoles();
    return roles.every(role => userRoles.includes(role.replace('ROLE_', '')));
  }

  closeDialogIfLoggedOut(dialogRef: MatDialogRef<any>): void {
    this.isLoggedIn$
      .pipe(
        filter(isLoggedIn => !isLoggedIn),
        take(1) // close once, then auto-unsubscribe
      )
      .subscribe(() => {
        dialogRef.close(false);
      });
  }

  get isSecureUser(): boolean {
    return this.hasAnyRole(['SecuredAssetCreate', 'SecuredAssetUpdate', 'SecuredAssetDelete']);
  }

  isCurrentUser(userId: string): boolean {
    const current = this.userInfoSubject.value;
    return !!current && current.id === userId;
  }

  updateCurrentUserInfo(user: User): void {
    const userInfo: UserInfo = {
      id: user.id,
      email: user.email,
      fName: user.firstName,
      lName: user.lastName || '',
      userRoles: user.childRoleInternalCodes || [],
      kiClient: user.kiClient,
      profilePic: user.images?.length ? this.sharedService.resolveFileImageSrc(user.images[0], environment.tempImageUrl) : ''
    };
    // CRITICAL: Store roles to localStorage BEFORE emitting userInfo$
    // This ensures that when side-nav reacts to userInfo$ emission,
    // getUserRoles() will return actual roles, not an empty array
    if (user.childRoleInternalCodes && user.childRoleInternalCodes.length > 0) {
      const rolesForStorage = user.childRoleInternalCodes.map((role: string) => role.replace('ROLE_', ''));
      localStorage.setItem('userRoles', JSON.stringify(rolesForStorage));
    }
    this.userInfoSubject.next(userInfo);
  }

  updateLocalProfile(profile: any): void {
    localStorage.setItem('kiClient', JSON.stringify(profile.kiClient));
    localStorage.setItem('userId', profile.id);
    localStorage.setItem('profilePic', profile.images?.length ? this.sharedService.resolveFileImageSrc(profile.images[0], environment.tempImageUrl) : '');
    if (profile.childRoleInternalCodes) {
      profile.childRoleInternalCodes = profile.childRoleInternalCodes.map((element: string) => {
        return element.replace('ROLE_', '');
      });
      localStorage.setItem('userRoles', JSON.stringify(profile.childRoleInternalCodes));
    }
    localStorage.setItem('email', profile.email);
    if (profile.mobile) {
      localStorage.setItem('mobile', profile.mobile);
    }
    localStorage.setItem('fName', profile.firstName);
    localStorage.setItem('lName', profile.lastName ?? '');
    localStorage.setItem('passwordExpiryDate', JSON.stringify(profile.passwordExpiryDate ?? ''));
    localStorage.setItem('passwordExpiryDateInMillis', new Date(profile.passwordExpiryDate).getTime().toString());
    this.sharedService.updateKiIndustryType(profile.kiClient?.kiIndustryType?.name?.toUpperCase() || 'NDT');
    localStorage.setItem('colourTheme', profile.kiClient?.colourTheme ?? '');
  }
}
